import React, { useEffect, useMemo, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import TimezoneSelect, { allTimezones } from 'react-timezone-select';
import { useForm, Controller } from 'react-hook-form';

import { filterTimeZones } from 'utils/utils';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { confirmDialog } from 'primereact/confirmdialog';

// Hoisted static list to avoid re-creating on each render
const DAYS = [
  { key: 'mon', label: 'MON' },
  { key: 'tue', label: 'TUE' },
  { key: 'wed', label: 'WED' },
  { key: 'thu', label: 'THU' },
  { key: 'fri', label: 'FRI' },
  { key: 'sat', label: 'SAT' },
  { key: 'sun', label: 'SUN' },
];

function DeliverySettingsSection({ title = 'Delivery Settings', onSave, onReset, initialValues, saving = false }) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: {
      startTime: null,
      endTime: null,
      timeZone: '',
      days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    },
    mode: 'onChange',
  });

  const timeZones = useMemo(() => filterTimeZones(allTimezones), []);

  const hasInitialData = useMemo(() => {
    if (!initialValues) return false;
    const hasTimes = initialValues?.startTime instanceof Date || initialValues?.endTime instanceof Date;
    const hasTimeZone = (() => {
      const tz = initialValues?.timeZone;
      if (!tz) return false;
      if (typeof tz === 'string') return tz.trim() !== '';
      return Boolean(tz?.value);
    })();
    const hasDays = Array.isArray(initialValues?.days) && initialValues.days.length > 0;
    return hasTimes || hasTimeZone || hasDays;
  }, [initialValues]);

  useEffect(() => {
    if (initialValues) {
      reset(
        {
          startTime: initialValues?.startTime ?? null,
          endTime: initialValues?.endTime ?? null,
          timeZone: initialValues?.timeZone ?? '',
          days: initialValues?.days ?? ['mon', 'tue', 'wed', 'thu', 'fri'],
        },
        { keepDirty: false }
      );
    }
  }, [initialValues, reset]);

  const selectedDays = watch('days') || [];

  const toggleDay = useCallback(
    (key) => {
      const current = selectedDays;
      const updated = current.includes(key) ? current.filter((d) => d !== key) : [...current, key];
      setValue('days', updated, { shouldDirty: true, shouldValidate: true });
    },
    [selectedDays, setValue]
  );

  const onSubmit = (data) => {
    if (onSave) onSave(data);
    reset(data, { keepDirty: false });
  };

  const onResetClick = useCallback(() => {
    const clearValues = {
      startTime: null,
      endTime: null,
      timeZone: '',
      days: [],
    };
    if (onReset) {
      const maybePromise = onReset();
      if (maybePromise && typeof maybePromise.then === 'function') {
        return maybePromise.then(() => {
          reset(clearValues, { keepDirty: false });
        });
      }
    }
    // Fallback: local-only reset
    reset(clearValues, { keepDirty: true });
  }, [onReset, reset]);

  const confirmReset = () => {
    confirmDialog({
      header: 'Confirm Reset',
      message: 'Are you sure you want to reset?',
      icon: 'pi pi-exclamation-triangle',
      accept: onResetClick,
      reject: () => null,
    });
  };

  return (
    <div className="card-bg p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="caption-bold">{title}</div>
        <div className="d-flex align-items-center">
          <Button
            label="Reset"
            className="p-button p-button-primary outline me-2"
            onClick={confirmReset}
            disabled={!hasInitialData || saving}
          />
          <Button
            label="Save Settings"
            className="p-button p-button-primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty || saving}
          />
        </div>
      </div>

      <div className="row align-items-center mb-3">
        <div className="col-md-2 col-12 p-medium">Time Range</div>
        <div className="col-md-10 col-12 d-flex flex-wrap gap-2">
          <div className="me-2" style={{ minWidth: 220 }}>
            <Controller
              name="startTime"
              control={control}
              rules={{
                required: 'Start time is required',
                validate: (v) => {
                  const end = watch('endTime');
                  if (v && end) {
                    return (
                      (v instanceof Date && end instanceof Date && v.getTime() < end.getTime()) || 'Start time must be before end time'
                    );
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Calendar
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.value);
                    trigger('endTime');
                  }}
                  timeOnly
                  hourFormat="12"
                  stepMinute={30}
                  placeholder="Start Time"
                  className="input-shadow w-100"
                />
              )}
            />
            <RequiredValidation errors={errors} field="startTime" message="Start time is required" />
          </div>
          <div style={{ minWidth: 220 }}>
            <Controller
              name="endTime"
              control={control}
              rules={{
                required: 'End time is required',
                validate: (v) => {
                  const start = watch('startTime');
                  if (v && start) {
                    return (
                      (v instanceof Date && start instanceof Date && start.getTime() < v.getTime()) || 'End time must be after start time'
                    );
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Calendar
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.value);
                    trigger('startTime');
                  }}
                  timeOnly
                  hourFormat="12"
                  stepMinute={30}
                  placeholder="End Time"
                  className="input-shadow w-100"
                />
              )}
            />
            <RequiredValidation errors={errors} field="endTime" message="End time is required" />
          </div>
        </div>
      </div>

      <div className="row align-items-center mb-3">
        <div className="col-md-2 col-12 p-medium">Time Zone</div>
        <div className="col-md-10 col-12 ">
          <Controller
            name="timeZone"
            control={control}
            rules={{
              validate: (v) => {
                if (!v) return 'Time zone is required';
                if (typeof v === 'string') return v?.trim() !== '' || 'Time zone is required';
                return Boolean(v?.value) || 'Time zone is required';
              },
            }}
            render={({ field }) => (
              <TimezoneSelect
                value={field.value}
                onChange={field.onChange}
                labelStyle="altName"
                timezones={{
                  ...timeZones,
                }}
                className={'p-dropdown w-100 time-zone-input '}
                styles={{
                  control: (base) => ({
                    ...base,
                    cursor: 'pointer',
                  }),
                }}
              />
            )}
          />
          <RequiredValidation errors={errors} field="timeZone" message="Time zone is required" />
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col-md-2 col-12 p-medium">Days</div>
        <div className="col-md-10 col-12 d-flex flex-wrap">
          {DAYS.map((d) => {
            const isSelected = selectedDays.includes(d.key);
            return (
              <Button
                key={d.key}
                label={d.label}
                style={{ height: '32px' }}
                className={isSelected ? 'p-button p-button-primary me-2 mb-2' : 'p-button p-button-secondary outline me-2 mb-2'}
                onClick={() => toggleDay(d.key)}
              />
            );
          })}
          <Controller
            name="days"
            control={control}
            rules={{
              validate: (v) => (Array.isArray(v) && v.length > 0) || 'Select at least one day',
            }}
            render={() => null}
          />
          <RequiredValidation errors={errors} field="days" message="Select at least one day" />
        </div>
      </div>
    </div>
  );
}

export default React.memo(DeliverySettingsSection);
